import { ISSUE_TYPE_CONFIG, PRIORITY_COLORS, isPastDate } from "@/lib/constants";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { CalendarDays } from "lucide-react";
import { format } from "date-fns";

export default function KanbanCard({ issue, users, isDragging, onClick }) {
  const typeConf = ISSUE_TYPE_CONFIG[issue.type] || {};
  const priorityConf = PRIORITY_COLORS[issue.priority] || {};
  const assignee = users.find((u) => u.id === issue.assignee_id);

  // For initiatives, resolve child task owners
  const childOwners = [];
  if (issue.type === "INITIATIVE" && issue.child_issue_keys?.length > 0) {
    // We don't have child data on the list endpoint, so we show the main assignee
    // The IssuePanel fetches children with full data
  }

  const dueDatePast = isPastDate(issue.due_date);
  const releaseDatePast = isPastDate(issue.release_date);

  return (
    <div
      data-testid={`kanban-card-${issue.issue_key}`}
      onClick={onClick}
      className={`p-3 bg-white border border-gray-200 rounded-sm shadow-sm hover:border-brand-rose transition-colors cursor-pointer flex flex-col gap-2.5 group ${
        isDragging ? "kanban-card-dragging" : ""
      }`}
    >
      {/* Top row: type badge + issue key + project key */}
      <div className="flex items-center gap-2">
        <span
          className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm"
          style={{ backgroundColor: typeConf.bg, color: typeConf.text }}
        >
          {typeConf.label}
        </span>
        <span className="text-[10px] font-mono text-gray-400">{issue.issue_key}</span>
        {issue.environment && (
          <span className="text-[10px] font-bold uppercase px-1 py-0.5 rounded-sm bg-gray-100 text-gray-500 border border-gray-200 ml-auto">
            {issue.environment}
          </span>
        )}
      </div>

      {/* Title */}
      <p className="text-sm font-medium text-black leading-snug">{issue.title}</p>

      {/* Bottom row: priority, due date, assignee */}
      <div className="flex items-center justify-between mt-auto">
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm border"
            style={{
              backgroundColor: priorityConf.bg,
              color: priorityConf.text,
              borderColor: priorityConf.border,
            }}
          >
            {issue.priority}
          </span>
          {issue.due_date && (
            <span
              className={`flex items-center gap-1 text-[10px] ${
                dueDatePast ? "text-red-600 font-bold" : "text-gray-500"
              }`}
            >
              <CalendarDays className="w-3 h-3" />
              {format(new Date(issue.due_date), "MMM d")}
            </span>
          )}
        </div>
        {assignee && (
          <Avatar className="h-6 w-6 border border-gray-200">
            <AvatarImage src={assignee.avatar_url} alt={assignee.full_name} />
            <AvatarFallback className="text-[10px] font-semibold">
              {assignee.full_name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
        )}
      </div>
    </div>
  );
}
